import { MarkerType } from '@vue-flow/core'

export const WORKFLOW_BLOCKS = [
  {
    type: 'start',
    label: 'Start',
    title: 'Start conversation',
    description: 'Open the call and greet the caller.',
    icon: '▶',
    tone: 'emerald',
    kind: 'entry',
  },
  {
    type: 'message',
    label: 'Message',
    title: 'Scripted message',
    description: 'A prepared line the agent says.',
    icon: '✎',
    tone: 'blue',
  },
  {
    type: 'voice',
    label: 'Voice',
    title: 'Voice profile',
    description: 'Language, voice and speaking style.',
    icon: '◌',
    tone: 'violet',
  },
  {
    type: 'collect',
    label: 'Collect info',
    title: 'Collect caller details',
    description: 'Ask for the data you need to move forward.',
    icon: '⟡',
    tone: 'sky',
  },
  {
    type: 'decision',
    label: 'Decision',
    title: 'Make a decision',
    description: 'Choose a branch based on the caller or data.',
    icon: '?',
    tone: 'amber',
  },
  {
    type: 'knowledge',
    label: 'Knowledge answer',
    title: 'Answer from knowledge',
    description: 'Reply using the knowledge base.',
    icon: '▤',
    tone: 'emerald',
  },
  {
    type: 'booking',
    label: 'Booking',
    title: 'Book appointment',
    description: 'Create or confirm the appointment.',
    icon: '▣',
    tone: 'cyan',
  },
  {
    type: 'payment',
    label: 'Payment / deposit',
    title: 'Request payment',
    description: 'Ask for a deposit or send a payment link.',
    icon: '$',
    tone: 'rose',
  },
  {
    type: 'transfer',
    label: 'Transfer',
    title: 'Transfer to human',
    description: 'Move the call to a person or team.',
    icon: '⇄',
    tone: 'slate',
  },
  {
    type: 'contact',
    label: 'SMS / Email',
    title: 'Send follow-up',
    description: 'Send a confirmation or recap.',
    icon: '✉',
    tone: 'indigo',
  },
  {
    type: 'webhook',
    label: 'Webhook / API',
    title: 'External action',
    description: 'Trigger an external system or API.',
    icon: '⌁',
    tone: 'cyan',
  },
  {
    type: 'end',
    label: 'End',
    title: 'End conversation',
    description: 'Close the interaction cleanly.',
    icon: '■',
    tone: 'emerald',
    kind: 'terminal',
  },
]

const BASE_CONFIG = {
  start: {
    greeting: 'Hello, thanks for calling.',
    language: 'english',
  },
  message: {
    text: 'How can I help you today?',
    voiceTone: 'professional',
  },
  voice: {
    language: 'english',
    providerVoiceId: '',
    voiceLabel: '',
    speakingStyle: 'clear',
  },
  collect: {
    fields: ['name', 'phone', 'service'],
    requiredFields: ['name', 'phone'],
    prompt: 'Please share the details we need to continue.',
  },
  decision: {
    branches: [
      { id: 'yes', label: 'Yes', keywords: ['yes', 'book', 'need', 'want'] },
      { id: 'no', label: 'No', keywords: ['no', 'later', 'not now', 'stop'] },
    ],
    fallbackLabel: 'Ask for more details',
  },
  knowledge: {
    baseName: 'Default knowledge base',
    fallbackMessage: 'I can check that for you.',
  },
  booking: {
    service: 'General consultation',
    duration: 30,
    calendar: 'Primary calendar',
    collect: ['name', 'phone', 'date', 'service'],
    confirmationChannel: 'sms',
  },
  payment: {
    mode: 'deposit',
    amount: 50,
    percentage: 20,
    currency: 'USD',
    message: 'I can send a payment link now.',
  },
  transfer: {
    target: 'Reception',
    condition: 'Caller requests a human',
    announce: 'I will transfer you now.',
  },
  contact: {
    channel: 'sms',
    subject: 'Appointment confirmation',
    template: 'Thanks for contacting us.',
  },
  webhook: {
    method: 'POST',
    url: '',
    secret: '',
  },
  end: {
    closing: 'Thanks, have a great day.',
  },
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function uid(prefix = 'id') {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

function mergeConfig(kind, overrides = {}) {
  return {
    ...clone(BASE_CONFIG[kind] || {}),
    ...(overrides || {}),
  }
}

export function paletteForKind(kind) {
  return WORKFLOW_BLOCKS.find((item) => item.type === kind) || WORKFLOW_BLOCKS[0]
}

export function createNode(kind, position = { x: 0, y: 0 }, overrides = {}) {
  const palette = paletteForKind(kind)
  return {
    id: overrides.id || uid(kind),
    type: 'workflowNode',
    position: clone(position),
    data: {
      kind,
      title: overrides.title || palette.title,
      description: overrides.description || palette.description,
      icon: overrides.icon || palette.icon,
      tone: overrides.tone || palette.tone,
      role: overrides.role || kind,
      config: mergeConfig(kind, overrides.config),
      branches: clone(overrides.branches || []),
      badges: clone(overrides.badges || []),
      summary: overrides.summary || '',
    },
  }
}

export function createEdge(source, target, options = {}) {
  return {
    id: options.id || uid('edge'),
    source,
    target,
    sourceHandle: options.sourceHandle || null,
    targetHandle: options.targetHandle || null,
    label: options.label || '',
    condition: options.condition || '',
    type: options.type || 'smoothstep',
    animated: Boolean(options.animated),
    markerEnd: options.markerEnd || { type: MarkerType.Arrow },
    style: {
      stroke: options.stroke || '#94a3b8',
      strokeWidth: options.strokeWidth || 2,
    },
  }
}

export function createBlankWorkflow(agentId) {
  const start = createNode('start', { x: 360, y: 40 })
  const message = createNode('message', { x: 350, y: 210 }, {
    title: 'Greet the caller',
    description: 'Open the call with the agent greeting.',
    config: { text: 'Hello, thanks for calling. How can I help you today?' },
  })
  const decision = createNode('decision', { x: 340, y: 390 }, {
    title: 'Do we have enough info?',
    description: 'Decide whether to answer or ask for more details.',
  })
  const collect = createNode('collect', { x: 70, y: 610 }, {
    title: 'Collect caller details',
  })
  const knowledge = createNode('knowledge', { x: 610, y: 610 }, {
    title: 'Answer from knowledge base',
  })
  const end = createNode('end', { x: 360, y: 900 })

  const edges = [
    createEdge(start.id, message.id, { sourceHandle: 'out', targetHandle: 'in', label: 'Next' }),
    createEdge(message.id, decision.id, { sourceHandle: 'out', targetHandle: 'in', label: 'Next' }),
    createEdge(decision.id, collect.id, { sourceHandle: 'yes', targetHandle: 'in', label: 'Yes' }),
    createEdge(decision.id, knowledge.id, { sourceHandle: 'no', targetHandle: 'in', label: 'No' }),
    createEdge(collect.id, end.id, { sourceHandle: 'out', targetHandle: 'in', label: 'Continue' }),
    createEdge(knowledge.id, end.id, { sourceHandle: 'out', targetHandle: 'in', label: 'Continue' }),
  ]

  return {
    id: null,
    agent_id: agentId || null,
    name: 'Untitled workflow',
    description: 'A simple conversation path for a new agent.',
    status: 'draft',
    version: 1,
    nodes: [start, message, decision, collect, knowledge, end],
    edges,
    settings: {
      snapToGrid: true,
      gridSize: 20,
      autoFit: true,
    },
    validation_status: 'draft',
  }
}

export function cloneNode(node, positionOffset = { x: 300, y: 40 }) {
  return {
    ...clone(node),
    id: uid(node?.data?.kind || 'node'),
    position: {
      x: Number(node?.position?.x || 0) + positionOffset.x,
      y: Number(node?.position?.y || 0) + positionOffset.y,
    },
  }
}

function parseJsonMaybe(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string') return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export function normalizeWorkflowRecord(record, agentId) {
  const parsed = parseJsonMaybe(record?.steps)
  const legacySteps = Array.isArray(parsed) ? parsed : Array.isArray(record?.steps) ? record.steps : null

  if (parsed && !Array.isArray(parsed)) {
    const workflow = parsed.workflow || {}
    return {
      id: record.id,
      agent_id: record.agent_id || agentId || null,
      name: record.name || workflow.name || 'Untitled workflow',
      description: workflow.description || '',
      status: workflow.status || 'draft',
      version: workflow.version || 1,
      nodes: Array.isArray(parsed.nodes) ? parsed.nodes : [],
      edges: Array.isArray(parsed.edges) ? parsed.edges : [],
      settings: parsed.settings || {},
      validation_status: workflow.validation_status || parsed.validation_status || 'draft',
      raw: parsed,
    }
  }

  if (legacySteps) {
    const nodes = legacySteps.map((step, index) => createNode('message', { x: 340, y: 120 + index * 160 }, {
      id: step.id || uid('legacy'),
      title: step.title || step.name || `Step ${index + 1}`,
      description: step.description || '',
      config: { text: step.text || step.content || '' },
    }))

    const edges = nodes.slice(1).map((node, index) => createEdge(nodes[index].id, node.id, { label: 'Next' }))

    return {
      id: record.id,
      agent_id: record.agent_id || agentId || null,
      name: record.name || 'Untitled workflow',
      description: '',
      status: 'draft',
      version: 1,
      nodes,
      edges,
      settings: {},
      validation_status: 'draft',
      raw: parsed,
    }
  }

  return createBlankWorkflow(agentId)
}

export function serializeWorkflowDocument(workflow) {
  return {
    workflow: {
      id: workflow.id || null,
      agent_id: workflow.agent_id || null,
      name: workflow.name || 'Untitled workflow',
      description: workflow.description || '',
      status: workflow.status || 'draft',
      version: workflow.version || 1,
      validation_status: workflow.validation_status || 'draft',
    },
    settings: workflow.settings || {},
    nodes: clone(workflow.nodes || []),
    edges: clone(workflow.edges || []),
  }
}

function edgeKey(edge) {
  return `${edge.source}:${edge.sourceHandle || 'out'}->${edge.target}:${edge.targetHandle || 'in'}`
}

function buildAdjacency(edges) {
  const outgoing = new Map()
  const incoming = new Map()

  edges.forEach((edge) => {
    if (!outgoing.has(edge.source)) outgoing.set(edge.source, [])
    outgoing.get(edge.source).push(edge)

    if (!incoming.has(edge.target)) incoming.set(edge.target, [])
    incoming.get(edge.target).push(edge)
  })

  return { outgoing, incoming }
}

function collectReachableNodes(startIds, edges) {
  const { outgoing } = buildAdjacency(edges)
  const visited = new Set()
  const stack = [...startIds]

  while (stack.length) {
    const current = stack.pop()
    if (!current || visited.has(current)) continue
    visited.add(current)
    const nextEdges = outgoing.get(current) || []
    nextEdges.forEach((edge) => stack.push(edge.target))
  }

  return visited
}

function inferMissingFields(node) {
  if (!node?.data?.config) return []
  const config = node.data.config
  if (node.data.kind === 'collect') {
    const required = Array.isArray(config.requiredFields) ? config.requiredFields : []
    return required.filter((field) => !field)
  }
  if (node.data.kind === 'booking') {
    const missing = []
    if (!config.service) missing.push('service')
    if (!config.duration) missing.push('duration')
    if (!config.calendar) missing.push('calendar')
    return missing
  }
  if (node.data.kind === 'payment') {
    const missing = []
    if (config.mode === 'deposit' && !config.amount && !config.percentage) missing.push('deposit amount or percentage')
    if (config.mode === 'full' && !config.amount) missing.push('amount')
    return missing
  }
  if (node.data.kind === 'webhook' && !config.url) return ['webhook url']
  return []
}

export function validateWorkflow(workflow) {
  const nodes = workflow.nodes || []
  const edges = workflow.edges || []
  const issues = []
  const { incoming, outgoing } = buildAdjacency(edges)
  const startNodes = nodes.filter((node) => node.data?.kind === 'start')
  const endNodes = nodes.filter((node) => node.data?.kind === 'end')

  if (!startNodes.length) {
    issues.push({ level: 'error', code: 'missing-start', title: 'Missing Start block', detail: 'Add a Start block to begin the flow.' })
  }

  if (!endNodes.length) {
    issues.push({ level: 'warning', code: 'missing-end', title: 'No End block', detail: 'Add an End block so the flow has a clear finish.' })
  }

  nodes.forEach((node) => {
    if (!node.data?.title || !String(node.data.title).trim()) {
      issues.push({ level: 'error', code: `blank-${node.id}`, title: 'Block without a name', detail: 'Every block needs a clear label.' })
    }

    const hasIncoming = (incoming.get(node.id) || []).length > 0
    const hasOutgoing = (outgoing.get(node.id) || []).length > 0

    if (node.data?.kind !== 'start' && !hasIncoming && !hasOutgoing) {
      issues.push({ level: 'warning', code: `isolated-${node.id}`, title: 'Unconnected block', detail: `“${node.data.title}” is not connected to anything.` })
    }

    if (node.data?.kind === 'decision') {
      const branchEdges = outgoing.get(node.id) || []
      if (branchEdges.length < 2) {
        issues.push({ level: 'error', code: `decision-${node.id}`, title: 'Decision needs branches', detail: 'A decision block should have at least two outgoing branches.' })
      }
      branchEdges.forEach((edge) => {
        if (!edge.label) {
          issues.push({ level: 'warning', code: edgeKey(edge), title: 'Branch without label', detail: 'Label branches clearly, such as Yes / No.' })
        }
      })
    }

    const missingFields = inferMissingFields(node)
    if (missingFields.length) {
      issues.push({
        level: 'error',
        code: `fields-${node.id}`,
        title: `${node.data.title} is missing settings`,
        detail: missingFields.join(', '),
      })
    }
  })

  const reachable = collectReachableNodes(startNodes.map((node) => node.id), edges)
  nodes.forEach((node) => {
    if (startNodes.length && !reachable.has(node.id)) {
      issues.push({
        level: 'warning',
        code: `unreachable-${node.id}`,
        title: 'Unreachable block',
        detail: `“${node.data.title}” cannot be reached from Start.`,
      })
    }
  })

  const status = issues.some((item) => item.level === 'error')
    ? 'error'
    : issues.some((item) => item.level === 'warning')
      ? 'warning'
      : 'ok'

  return { issues, status }
}

function findNodeByKind(nodes, kind) {
  return nodes.find((node) => node.data?.kind === kind)
}

function matchDecisionBranch(node, messageText) {
  const branches = node.data?.config?.branches || []
  const lower = String(messageText || '').toLowerCase()
  const hit = branches.find((branch) => {
    const keywords = branch.keywords || []
    return keywords.some((keyword) => lower.includes(String(keyword).toLowerCase()))
  })
  return hit || branches[0] || null
}

function summarizeNodeAction(node, context) {
  const text = String(context.messageText || '')
  const config = node.data?.config || {}
  switch (node.data?.kind) {
    case 'start':
      return { action: 'start', response: config.greeting || 'Hello.', missingFields: [] }
    case 'message':
      return { action: 'message', response: config.text || 'How can I help?', missingFields: [] }
    case 'voice':
      return {
        action: 'voice',
        response: `Using ${config.voiceLabel || config.providerVoiceId || 'the selected voice'}.`,
        missingFields: config.providerVoiceId ? [] : ['voice'],
      }
    case 'collect': {
      const required = config.requiredFields || []
      const missing = required.filter((field) => !text.toLowerCase().includes(field))
      return {
        action: 'collect',
        response: config.prompt || 'Please share the missing details.',
        missingFields: missing,
      }
    }
    case 'decision': {
      const branch = matchDecisionBranch(node, text)
      return {
        action: 'decision',
        branch: branch ? branch.label : 'Fallback',
        response: branch ? `Taking the ${branch.label} branch.` : 'Asking for more details.',
        missingFields: [],
      }
    }
    case 'knowledge':
      return {
        action: 'knowledge',
        response: config.fallbackMessage || 'I can check that for you.',
        missingFields: [],
      }
    case 'booking': {
      const missing = []
      if (!text.toLowerCase().includes('name')) missing.push('name')
      if (!text.toLowerCase().includes('phone')) missing.push('phone')
      if (!text.toLowerCase().includes('date')) missing.push('date')
      if (!text.toLowerCase().includes('service')) missing.push('service')
      return {
        action: 'booking',
        response: `Booking a ${config.service || 'service'} appointment.`,
        missingFields: missing,
      }
    }
    case 'payment':
      return {
        action: 'payment',
        response: config.message || 'A payment link will be sent.',
        missingFields: config.mode === 'deposit' && !config.amount && !config.percentage ? ['amount'] : [],
      }
    case 'transfer':
      return {
        action: 'transfer',
        response: config.announce || 'Transferring the call.',
        missingFields: [],
      }
    case 'contact':
      return {
        action: config.channel === 'email' ? 'email' : 'sms',
        response: config.template || 'Sending a follow-up message.',
        missingFields: [],
      }
    case 'webhook':
      return {
        action: 'webhook',
        response: config.url ? `Calling ${config.url}` : 'Triggering an external action.',
        missingFields: config.url ? [] : ['url'],
      }
    case 'end':
      return { action: 'end', response: config.closing || 'Thanks, goodbye.', missingFields: [] }
    default:
      return { action: 'unknown', response: '', missingFields: [] }
  }
}

export function simulateWorkflow(workflow, messageText) {
  const nodes = workflow.nodes || []
  const edges = workflow.edges || []
  const { outgoing } = buildAdjacency(edges)
  const startNode = findNodeByKind(nodes, 'start') || nodes[0]
  const visited = []
  const actions = []
  const missing = new Set()
  let current = startNode || null
  let branch = ''
  let response = ''
  const maxSteps = 12
  let steps = 0

  while (current && steps < maxSteps) {
    steps += 1
    visited.push(current.id)
    const outcome = summarizeNodeAction(current, { messageText, workflow })
    if (outcome.response) response = outcome.response
    outcome.missingFields.forEach((field) => missing.add(field))
    actions.push({
      nodeId: current.id,
      kind: current.data?.kind || 'unknown',
      title: current.data?.title || 'Untitled',
      action: outcome.action,
      branch: outcome.branch || '',
      response: outcome.response || '',
    })

    if (current.data?.kind === 'end') break

    const nextEdges = outgoing.get(current.id) || []
    if (!nextEdges.length) break

    let nextEdge = nextEdges[0]
    if (current.data?.kind === 'decision') {
      const branchMatch = matchDecisionBranch(current, messageText)
      if (branchMatch) {
        branch = branchMatch.label
        const matched = nextEdges.find((edge) => String(edge.label || '').toLowerCase() === String(branchMatch.label || '').toLowerCase())
        if (matched) nextEdge = matched
      }
    }

    const nextNode = nodes.find((node) => node.id === nextEdge.target)
    if (!nextNode) break
    current = nextNode
  }

  return {
    visited,
    branch,
    response: response || 'No response generated.',
    missingFields: Array.from(missing),
    actions,
    activeNode: current ? {
      id: current.id,
      title: current.data?.title || '',
      kind: current.data?.kind || '',
    } : null,
  }
}

export function autoLayoutWorkflow(workflow) {
  const nodes = clone(workflow.nodes || [])
  const edges = workflow.edges || []
  const { outgoing, incoming } = buildAdjacency(edges)
  const levels = new Map()
  const queue = []
  const startNodes = nodes.filter((node) => node.data?.kind === 'start')

  if (!startNodes.length) {
    nodes.forEach((node, index) => {
      node.position = { x: 240 + (index % 2) * 360, y: 120 + Math.floor(index / 2) * 180 }
    })
    return { ...workflow, nodes }
  }

  startNodes.forEach((node) => {
    levels.set(node.id, 0)
    queue.push(node.id)
  })

  while (queue.length) {
    const nodeId = queue.shift()
    const level = levels.get(nodeId) || 0
    const nextEdges = outgoing.get(nodeId) || []
    nextEdges.forEach((edge) => {
      const nextLevel = level + 1
      if (!levels.has(edge.target) || levels.get(edge.target) > nextLevel) {
        levels.set(edge.target, nextLevel)
      }
      queue.push(edge.target)
    })
  }

  nodes.forEach((node) => {
    if (!levels.has(node.id)) {
      const fallback = incoming.has(node.id) ? 3 : 0
      levels.set(node.id, fallback)
    }
  })

  const buckets = new Map()
  nodes.forEach((node) => {
    const level = levels.get(node.id) || 0
    if (!buckets.has(level)) buckets.set(level, [])
    buckets.get(level).push(node)
  })

  Array.from(buckets.entries()).forEach(([level, bucket]) => {
    bucket.forEach((node, index) => {
      const branchOffset = node.data?.kind === 'decision' ? 100 : 0
      node.position = {
        x: 220 + level * 320 + branchOffset,
        y: 80 + index * 180 + (level % 2) * 30,
      }
    })
  })

  return { ...workflow, nodes }
}
